import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { NotifyOrConfirmType } from '../enums/notify-or-confirm-type.enum';
import { NotifyOrConfirmInterface } from '../interfaces/notify-or-confirm.interface';

/**
 * Service for notifications and confirmations
 *
 * Works together with the NotifyOrConfirm Component
 */
@Injectable({
	providedIn: 'root'
})
export class NotifyOrConfirmService {
	// Open / close modal
	protected modal$: BehaviorSubject<NotifyOrConfirmInterface> = new BehaviorSubject(null);

	// -----------------------------------------------------------------------------------------------------------------
	// Class variables
	// -----------------------------------------------------------------------------------------------------------------

	// Get modal value
	protected modalValue$: Subject<any> = new Subject();

	constructor(private translate: TranslateService) {}

	// -----------------------------------------------------------------------------------------------------------------
	// Methods
	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * Observer for ModalComponent
	 */
	public get modalObserver(): Observable<NotifyOrConfirmInterface> {
		return this.modal$.asObservable();
	}

	/**
	 * Observer for component which uses the modal
	 */
	public get modalValueObserver(): Observable<boolean> {
		return this.modalValue$.asObservable().pipe(first());
	}

	/**
	 * Opens a customizable notification
	 */
	showNotification(
		title: string,
		message: string,
		type: NotifyOrConfirmType = NotifyOrConfirmType.NOTIFICATION,
		buttonText: string = null
	) {
		setTimeout(() => {
			// Tell the modal component that the params have changed
			this.modal$.next({
				title,
				message,
				type,
				buttonText: buttonText ? buttonText : this.translate.instant('notificationOrConfirm.ok')
			});
		});
	}

	/**
	 * Opens a customizable confirmation
	 */
	showConfirmation(title: string, message: string, buttons: { confirmButtonText?: string; cancelButtonText?: string } = null) {
		setTimeout(() => {
			// Tell the modal component that the params have changed
			this.modal$.next({
				title,
				message,
				type: NotifyOrConfirmType.CONFIRMATION,
				buttons
			});
		});
	}

	/**
	 * Set value for current modal
	 */
	sendValue(val: boolean) {
		this.modalValue$.next(val);
	}

	/**
	 * Close the current notification or confirmation
	 */
	close() {
		this.modal$.next(null);
	}
}
